import React, { useState, useCallback, useMemo, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { FormattedMessage } from 'react-intl';
import withStyles from '@material-ui/core/styles/withStyles';
import Container from '@material-ui/core/Container';
import Hidden from '@material-ui/core/Hidden';
import toLower from 'lodash/toLower';
import isEmpty from 'lodash/isEmpty';
import ceil from 'lodash/ceil';
import qs from 'query-string';
import { /* GetApplicationTypeBySlugName, */ keyEmptyCheck } from 'aurora-utils/utils';
import messages from 'aurora-translations';
import history from 'aurora-utils/history';
import { useDeepEffect } from 'aurora-hooks';
import { propertySwitchTypes } from 'aurora-utils/constants';
import classNames from 'classnames';
import {
  fetchLeasePropertiesV2,
  fetchOwnerPropertiesV2,
  fetchMortgagePropertiesV2,
  fetchBuildingProperties as fetchBuildingPropertiesAction,
  fetchLeasePropertiesPMA,
  fetchPMCProperties,
} from 'aurora-composition/modules/properties/actions';
import { CLEAR_PROPERTIES_DATA } from 'aurora-composition/modules/properties/constants';
import // CleverTapGenericServiceSelectPropertyEvent,
// CleverTapPropertiesFlow,
// CleverTapPropertiesFilter,
'aurora-utils/cleverTap.eventhelper';
import { ADGMPagination, ADGMImage } from 'aurora-components';
import { ADGMFilters } from 'aurora-section-components';
import FILTERS_NAMES from 'aurora-utils/filtersNames';
import NoProperties from './NoProperty';
import NoPropertySelection from './NoPropertySelection';
import PropertyList from './PropertyList';
import styles from './styles';
import { ADGMButton } from '../../components';

function ADGMProperties(props) {
  const {
    classes,
    breadCrumbs,
    showTitle,
    requiredFilters,
    hidePropertyToggle,
    selectable,
    multi,
    switchType,
    isNewThreeFilterTabs,
    propertyType: propertyTypeProp,
    onNext,
    onBack,
    defaultFilters,
    defaultSelected,
    defaultSwitchType,
    state: routeState,
    isPMA,
    showSwitch,
    showMortgageProperties,
    onNextLoading,
    viewLeaseUnitsHandler,
    clearPropertiesOnUnMount,
    nextBtnText,
    backBtnText,
    enabledNextButton,
    isPMABank,
    isPMCUser,
    isSelectedPMAProperties,
    isSubLease,
  } = props;
  const {
    location: { search },
  } = history;
  const PAGE_SIZE = isPMABank ? 5 : 10;
  const [selected, setSelected] = useState(defaultSelected);
  const query = qs.parse(search);

  const { page = 1, ...filters } = query;
  const type = switchType || query.type || defaultSwitchType;
  // for land = 1 & for unit = 4
  let propertyType = propertyTypeProp || query.propertyType || '100';
  const isPmc = query.isPmc || (isPMCUser === true ? 'true' : 'false');
  const { properties, loading, error, locale /* slugName */ } = useSelector(
    createSelector(
      state => state.propertiesReducer,
      state => state.language,
      state => state.serviceReducer,
      (property, language, Service) => ({
        loading: property.loading,
        error: property.error,
        properties: property.properties,
        locale: language.locale,
        slugName: Service.slugName,
      }),
    ),
  );

  const dispatch = useDispatch();

  const isLease = useMemo(() => toLower(type) === toLower(propertySwitchTypes.LEASE), [type]);
  const isMultiLease = useMemo(
    () => toLower(type) === toLower(propertySwitchTypes.LEASE) && typeof viewLeaseUnitsHandler === 'function',
    [type],
  );

  if (!query.propertyType && isLease) propertyType = '4';

  const isOwnership = useMemo(() => toLower(type) === toLower(propertySwitchTypes.OWNERSHIP), [type]);

  const isMortgage = useMemo(() => showMortgageProperties && toLower(type) === toLower(propertySwitchTypes.MORTGAGE), [
    type,
    showMortgageProperties,
  ]);
  const isProperty = useMemo(() => toLower(type) === toLower(propertySwitchTypes.PROPERTY), [type]);
  const isProject = useMemo(() => toLower(type) === toLower(propertySwitchTypes.PROJECT), [type]);
  const isCertificate = useMemo(() => toLower(type) === toLower(propertySwitchTypes.CERTIFICATE), [type]);

  if (!query.propertyType && isMortgage) propertyType = null;

  useDeepEffect(() => {
    const payload = {
      page,
      PAGE_SIZE,
      propertyType,
      filters,
      defaultFilters,
      isPMABank,
    };
    if (isLease) {
      if (query?.callSecondApi === 'true') {
        if (isPMABank) {
          if (
            Object.keys(query).some(item =>
              ['plotNumber', 'unitNumber', 'buildingRegNumber', 'unitRegNo', 'premiseId'].includes(item),
            ) ||
            ['district', 'community'].every(item => Object.keys(query).includes(item))
          ) {
            dispatch(
              fetchLeasePropertiesPMA({
                ...payload,
                pageNumber: payload.page - 1,
                pageSize: payload.PAGE_SIZE,
                applicationId: query.id,
              }),
            );
          } else
            dispatch({
              type: CLEAR_PROPERTIES_DATA,
            });
        } else {
          dispatch(
            fetchLeasePropertiesPMA({
              ...payload,
              pageNumber: payload.page - 1,
              pageSize: payload.PAGE_SIZE,
              applicationId: query.id,
            }),
          );
        }
      } else if (isPmc === 'true') {
        dispatch(
          fetchPMCProperties({
            ...payload,
            pageNumber: payload.page - 1,
          }),
        );
      } else if (isPMABank) {
        if (
          Object.keys(query).some(item =>
            ['plotNumber', 'unitNumber', 'buildingRegNumber', 'unitRegNo', 'premiseId'].includes(item),
          ) ||
          ['district', 'community'].every(item => Object.keys(query).includes(item))
        ) {
          dispatch(
            fetchLeasePropertiesV2({
              ...payload,
              page: payload.page - 1,
            }),
          );
        } else {
          dispatch({
            type: CLEAR_PROPERTIES_DATA,
          });
        }
      } else {
        dispatch(
          fetchLeasePropertiesV2({
            ...payload,
            page: payload.page - 1,
          }),
        );
      }
    }
    if (isOwnership || isProperty || isProject || isCertificate) {
      dispatch(
        fetchOwnerPropertiesV2({
          ...payload,
          page: payload.page - 1,
        }),
      );
    }
  }, [search]);

  useEffect(() => {
    if (clearPropertiesOnUnMount === true) {
      return () => {
        dispatch({
          type: CLEAR_PROPERTIES_DATA,
        });
      };
    }
    return null;
  }, []);

  useDeepEffect(() => {
    const payload = { page, PAGE_SIZE, propertyType, filters, defaultFilters };
    if (isMortgage) {
      dispatch(
        fetchMortgagePropertiesV2({
          ...payload,
          page: payload.page - 1,
        }),
      );
    }
  }, [search, isMortgage]);

  // useEffect(() => {
  //   CleverTapPropertiesFlow(type);
  // }, [type]);
  const fetchBuildingProperties = useCallback(id => {
    return dispatch(fetchBuildingPropertiesAction(id));
  }, []);

  const switchOptions = useMemo(() => {
    if (showSwitch !== true) return [];

    const options = isNewThreeFilterTabs
      ? [
          {
            label: keyEmptyCheck('property'),
            value: toLower(propertySwitchTypes.PROPERTY),
          },
          {
            label: keyEmptyCheck('project'),
            value: toLower(propertySwitchTypes.PROJECT),
          },
          {
            label: keyEmptyCheck('certificate'),
            value: toLower(propertySwitchTypes.CERTIFICATE),
          },
        ]
      : [
          {
            label: keyEmptyCheck('Lease'),
            value: toLower(propertySwitchTypes.LEASE),
          },
          {
            label: keyEmptyCheck('Ownership'),
            value: toLower(propertySwitchTypes.OWNERSHIP),
          },
        ];

    if (showMortgageProperties && !isNewThreeFilterTabs) {
      options.push({
        label: keyEmptyCheck('mortgaged'),
        value: toLower(propertySwitchTypes.MORTGAGE),
      });
    }
    return options;
  }, [showMortgageProperties, isNewThreeFilterTabs]);

  const propertyFilterOptions = useMemo(() => {
    if (hidePropertyToggle) return [];
    const options = [
      ...(!isPMA && !isLease
        ? [
            {
              label: keyEmptyCheck('All'),
              value: isMortgage && propertyType === null ? null : '100',
            },
          ]
        : []),
      {
        label: keyEmptyCheck(isLease ? 'building' : 'Land'),
        value: isLease || isPMA ? '2' : '1',
      },
      { label: keyEmptyCheck('Unit'), value: '4' },
    ];

    return options;
  }, [isPMA, isLease, isMortgage, propertyType]);
  const requiredFiltersData = useMemo(() => {
    if (isLease && isEmpty(requiredFilters)) {
      return [
        FILTERS_NAMES.AUTHORIZATION,
        FILTERS_NAMES.POA_OWNERS,
        FILTERS_NAMES.PROPERTY_REG_NUMBER,
        FILTERS_NAMES.UNIT_REG_NUMBER,
        FILTERS_NAMES.PLOT_NUMBER,
        FILTERS_NAMES.OWNERSHIP_TYPE_ID,
        FILTERS_NAMES.DISTRICT,
        FILTERS_NAMES.COMMUNITY,
        FILTERS_NAMES.BEDROOMS,
        FILTERS_NAMES.BUILDING_NUMBER,
        FILTERS_NAMES.UNIT_NUMBER,
        FILTERS_NAMES.RENT_STATUS,
      ];
    }

    if (isOwnership && isEmpty(requiredFilters)) {
      let loggedInUser = null;
      if (localStorage.getItem('selectedAccount')) {
        const adddata = localStorage?.selectedAccount;
        loggedInUser = JSON.parse(adddata);
      }
      return [
        FILTERS_NAMES.AUTHORIZATION,
        FILTERS_NAMES.POA_OWNERS,
        FILTERS_NAMES.PLOT_NUMBER,
        FILTERS_NAMES.DISTRICT,
        FILTERS_NAMES.COMMUNITY,
        FILTERS_NAMES.OWNERSHIP_TYPE_ID,
        FILTERS_NAMES.ALLOCATION_TYPE,
        FILTERS_NAMES.ACQUISITION_TYPE,
        FILTERS_NAMES.MORTGAGE_STATUS,
        FILTERS_NAMES.PROJECT_STATUS,
        ...(loggedInUser && loggedInUser?.isDeveloper ? [FILTERS_NAMES.PROJECT_ELMS_ID] : []),
        ...(propertyType === '1' ? [FILTERS_NAMES.LAND_USE] : []),
        ...(propertyType === '100' || propertyType === '4'
          ? [FILTERS_NAMES.UNIT_NUMBER, FILTERS_NAMES.UNIT_USE, FILTERS_NAMES.UNIT_TYPE, FILTERS_NAMES.NO_OF_ROOMS]
          : []),
      ];
    }

    if (isMortgage && isEmpty(requiredFilters)) {
      let loggedInUser = null;
      if (localStorage.getItem('selectedAccount')) {
        const adddata = localStorage?.selectedAccount;
        loggedInUser = JSON.parse(adddata);
      }
      return [
        FILTERS_NAMES.AUTHORIZATION,
        FILTERS_NAMES.POA_OWNERS,
        FILTERS_NAMES.PLOT_NUMBER,
        FILTERS_NAMES.DISTRICT,
        FILTERS_NAMES.COMMUNITY,
        FILTERS_NAMES.OWNERSHIP_TYPE_ID,
        FILTERS_NAMES.ALLOCATION_TYPE,
        FILTERS_NAMES.ACQUISITION_TYPE,
        FILTERS_NAMES.PROJECT_STATUS,
        ...(loggedInUser && loggedInUser?.isDeveloper ? [FILTERS_NAMES.PROJECT_ELMS_ID] : []),
        ...(propertyType === '1' ? [FILTERS_NAMES.LAND_USE] : []),
        ...(propertyType === '100' || propertyType === '4'
          ? [FILTERS_NAMES.UNIT_NUMBER, FILTERS_NAMES.UNIT_USE, FILTERS_NAMES.UNIT_TYPE, FILTERS_NAMES.NO_OF_ROOMS]
          : []),
      ];
    }
    if (isNewThreeFilterTabs) {
      if (isProperty) {
        if (propertyTypeProp === 1) {
          return [FILTERS_NAMES.DISTRICT, FILTERS_NAMES.COMMUNITY, FILTERS_NAMES.PLOT_NUMBER, FILTERS_NAMES.LAND_USE];
        }
        return [
          FILTERS_NAMES.DISTRICT,
          FILTERS_NAMES.COMMUNITY,
          FILTERS_NAMES.PLOT_NUMBER,
          FILTERS_NAMES.BUILDING_NUMBER,
          FILTERS_NAMES.FLOOR_NUMBER,
          FILTERS_NAMES.UNIT_NUMBER,
          FILTERS_NAMES.UNIT_TYPE,
          FILTERS_NAMES.LAND_USE,
        ];
      }
      if (isProject) {
        return [
          FILTERS_NAMES.PROJECT_NAME,
          FILTERS_NAMES.PLOT_NUMBER,
          FILTERS_NAMES.BUILDING_NUMBER,
          FILTERS_NAMES.FLOOR_NUMBER,
          FILTERS_NAMES.UNIT_NUMBER,
        ];
      }
      if (isCertificate) {
        return [FILTERS_NAMES.CERTIFICATE_TYPE_V2, FILTERS_NAMES.CERTIFICATE_NUMBER, FILTERS_NAMES.CERTIFICATE_DATE];
      }
    }
    return requiredFilters;
  }, [isLease, isOwnership, isMortgage, requiredFilters, propertyType, isProperty, isProject, isCertificate]);

  const onNextDisabled = useMemo(() => {
    if (onNextLoading || loading || (isEmpty(selected) && enabledNextButton === false)) {
      return true;
    }
    return false;
  }, [selected, loading, onNextLoading]);

  const showFooter = useMemo(() => !isPMA && !isMultiLease && (selectable || multi), [isPMA, selected, multi]);
  const showPMAFooter = useMemo(() => isPMA && propertyType === '4', [isPMA, propertyType]);
  const totalPages = useMemo(() => ceil(properties?.totalCount / PAGE_SIZE), [properties]);

  const filtersApiParams = useMemo(() => {
    const params = {};
    if (requiredFilters?.includes(FILTERS_NAMES.PROJECT_NAME)) {
      params[FILTERS_NAMES.PROJECT_NAME] = {
        isElmsProjectId: true,
      };
    }

    return params;
  }, [requiredFilters]);

  const handleQueryChange = useCallback(
    newQuery => {
      history.replace({
        search: qs.stringify({ id: query.id, ...newQuery }),
        state: { ...routeState },
      });
      // CleverTapPropertiesFilter(newQuery);
    },
    [search],
  );

  const handleSwitchChange = useCallback(
    value => {
      const newQuery = { type: toLower(value), page: 1 };
      handleQueryChange(newQuery);
      // CleverTapPropertiesFlow(newQuery.type);
    },
    [query],
  );

  const handlePageChange = useCallback(
    num => {
      const newQuery = { ...query, page: num };
      handleQueryChange(newQuery);
    },
    [query],
  );

  const handleResetFilters = useCallback(() => {
    let newQuery = { type: query.type, page: 1 };
    if (query?.isRegistration || query?.isAmendment || query?.isRenewal) {
      newQuery = {
        ...newQuery,
        ...(query?.isRegistration && { isRegistration: query.isRegistration }),
        ...(query.isAmendment && { isAmendment: query?.isAmendment }),
        ...(query.isRenewal && { isRenewal: query?.isRenewal }),
        ...(query.callSecondApi && { callSecondApi: query?.callSecondApi }),
        ...(query?.isPMABank && { isPMABank: query.isPMABank }),
        propertyType: 2,
      };
    }
    handleQueryChange(newQuery);
  }, [query.type]);

  const handleNext = useCallback(() => {
    if (typeof onNext === 'function') {
      onNext(selected);
      // const applicationType = GetApplicationTypeBySlugName(slugName);
      // const CleverTapPayload = {
      //   propertyId: selected?.propertyID,
      //   applicationType,
      // };
      // CleverTapGenericServiceSelectPropertyEvent(CleverTapPayload);
    }
  }, [selected]);

  const addProperties = useCallback(() => {
    if (query?.isRegistration)
      isPMABank
        ? history.push('/app/services/pma-registration/' + query.id + `?isPMABank=${isPMABank}`)
        : history.push('/app/services/pma-registration/' + query.id);
    else if (query?.isAmendment) history.push('/app/services/pma-amendment/' + query.id);
    else if (query?.isRenewal) history.push('/app/services/pma-renewal/' + query.id);
    else console.log('no path match');
  }, [query]);

  const handlePropertyType = useCallback(
    id => {
      const newQuery = { ...query, page: 1, propertyType: id };
      handleQueryChange(newQuery);
    },
    [query],
  );

  const handleSearch = useCallback(
    appliedFilters => {
      const commonQuery = {
        page: 1,
        propertyType: query.propertyType,
        ...appliedFilters,
      };

      let newQuery = {};

      if (isLease) {
        if (isPmc === 'true') {
          newQuery = {
            ...commonQuery,
            isPmc: 'true',
            isPMA: 'true',
            ...(query?.isRegistration && { isRegistration: query.isRegistration }),
            ...(query?.isAmendment && { isAmendment: query.isAmendment }),
            ...(query?.isRenewal && { isRenewal: query.isRenewal }),
          };
        } else {
          newQuery = {
            ...commonQuery,
            type: 'lease',
            ...(query?.isRegistration && { isRegistration: query.isRegistration }),
            ...(query?.isAmendment && { isAmendment: query.isAmendment }),
            ...(query?.isRenewal && { isRenewal: query.isRenewal }),
            ...(query?.callSecondApi && { callSecondApi: query.callSecondApi }),
            ...(isPMABank && { isPMABank }),
            ...(isSelectedPMAProperties && { isSelectedPMAProperties }),
          };
        }
      } else if (isOwnership) {
        newQuery = { ...commonQuery, type: 'ownership' };
      } else if (isProperty) {
        newQuery = { ...commonQuery, type: 'property' };
      } else if (isProject) {
        newQuery = { ...commonQuery, type: 'project' };
      } else if (isCertificate) {
        newQuery = { ...commonQuery, type: 'certificate' };
      } else if (isMortgage) {
        newQuery = { ...commonQuery, type: 'mortgage' };
      }

      handleQueryChange(newQuery);
    },
    [query],
  );

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  return (
    <ADGMFilters
      loading={loading}
      breadCrumbs={breadCrumbs}
      // title={title ? keyEmptyCheck(title) : keyEmptyCheck('my_properties')}
      showTitle={showTitle}
      query={query}
      requiredFilters={requiredFiltersData}
      totalResults={properties?.totalCount}
      {...(showSwitch === true && {
        selectedSwitch: toLower(type),
        switchOptions,
        onSwitchChange: handleSwitchChange,
      })}
      {...(hidePropertyToggle !== true && {
        propertyFilterOptions,
        selectedPropertyFilter: propertyType,
        onPropertyFilterChange: handlePropertyType,
      })}
      error={error}
      onSearch={handleSearch}
      onFiltersReset={handleResetFilters}
      customClasses={{
        ...((showFooter || showPMAFooter) && { root: classes.paddingBottom }),
      }}
      filtersApiParams={filtersApiParams}
      filterTitle={isPmc === 'true' ? 'Search_properties' : 'Filters'}
      showClearAll={isPmc !== 'true'}
      filterBtnTitle={isPmc === 'true' ? 'search_text' : 'search'}
      filterModalTitle={isPmc === 'true' ? 'search_text' : 'show_more_filter'}
      autoOpenFilterModal={isPmc === 'true' && isMobile}
      isPMABank={isPMABank}
    >
      {/* Property List */}
      {isPMABank &&
      !loading &&
      !Object.keys(query).some(item =>
        ['plotNumber', 'unitNumber', 'buildingRegNumber', 'unitRegNo', 'premiseId'].includes(item),
      ) &&
      !['district', 'community'].every(item => Object.keys(query).includes(item)) ? (
        <NoPropertySelection isSelectedPMAProperties={isSelectedPMAProperties} />
      ) : (
        !loading &&
        isEmpty(properties?.properties) && <NoProperties isPmcUser={isPmc === 'true'} isSubLease={isSubLease} />
      )}

      {!isEmpty(properties) && (
        <PropertyList
          locale={locale}
          isLease={isLease}
          isMultiLease={isMultiLease}
          isOwnership={isOwnership || isProperty || isProject || isCertificate}
          isMortgage={isMortgage}
          selectable={selectable}
          multi={multi}
          selected={selected}
          setSelected={setSelected}
          data={properties}
          page={page}
          switchType={type}
          fetchBuildingProperties={fetchBuildingProperties}
          isPMA={isPMA}
          history={history}
          viewLeaseUnitsHandler={viewLeaseUnitsHandler}
          isPMABank={isPMABank}
          isPmc={isPmc}
        />
      )}

      {/* Pagination */}
      {!isEmpty(properties?.properties) && totalPages > 1 && (
        <div className={classes.paginationHolder}>
          <ADGMPagination
            totalPages={totalPages}
            currentPage={Number(page)}
            onPageChange={handlePageChange}
            customClasses={{ container: classes.pagination }}
          />
        </div>
      )}
      {/* Action buttons footer */}
      {showFooter && (
        <div
          className={classNames(classes.footer, {
            [classes.rotateArrow]: locale === 'ar',
          })}
        >
          <Container className={classes.container}>
            <ADGMButton
              color='primary'
              className={classes.nextBtn}
              variant='primary'
              onClick={handleNext}
              disabled={onNextDisabled}
              // endIcon={
              //   <Hidden xsDown>
              //     <ADGMImage name='NextArrow.png' alt='next-arrow-btn' />
              //   </Hidden>
              // }
            >
              {multi
                ? keyEmptyCheck(nextBtnText || 'selected_properties_count', {
                    count: selected?.length || 0,
                  })
                : keyEmptyCheck(nextBtnText || 'proceed')}
            </ADGMButton>
            <ADGMButton
              variant='secondary'
              className={classNames(classes.backButton, { [classes.rotateArrow]: locale !== 'ar' })}
              onClick={onBack}
              startIcon={
                backBtnText === 'previous' ? (
                  <Hidden xsDown>
                    <ADGMImage name='NextArrow.png' alt='next-arrow-btn' />
                  </Hidden>
                ) : (
                  <></>
                )
              }
            >
              {backBtnText ? keyEmptyCheck(backBtnText) : keyEmptyCheck('back')}
            </ADGMButton>
          </Container>
        </div>
      )}

      {showPMAFooter && (
        <div className={classes.pmaFooter}>
          <Container className={classes.container}>
            <ADGMButton variant='secondary' onClick={addProperties}>
              <FormattedMessage {...messages.cancel} />
            </ADGMButton>
            <ADGMButton variant='primary' onClick={addProperties} disabled={onNextDisabled}>
              {multi ? (
                keyEmptyCheck('add_selected_count', { s: selected?.length })
              ) : (
                <FormattedMessage {...messages.proceed} />
              )}
            </ADGMButton>
          </Container>
        </div>
      )}
    </ADGMFilters>
  );
}

ADGMProperties.propTypes = {
  classes: PropTypes.object.isRequired,
  breadCrumbs: PropTypes.array.isRequired,
  hidePropertyToggle: PropTypes.bool,
  title: PropTypes.string,
  showTitle: PropTypes.bool,
  requiredFilters: PropTypes.array,
  selectable: PropTypes.bool,
  multi: PropTypes.bool,
  switchType: PropTypes.string,
  propertyType: PropTypes.number,
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  defaultFilters: PropTypes.object,
  defaultSelected: PropTypes.object,
  defaultSwitchType: PropTypes.string,
  state: PropTypes.object,
  isPMA: PropTypes.bool,
  isMultiLease: PropTypes.bool,
  showSwitch: PropTypes.bool,
  showMortgageProperties: PropTypes.bool,
  onNextLoading: PropTypes.bool,
  viewLeaseUnitsHandler: PropTypes.oneOf[(PropTypes.func, null)],
  clearPropertiesOnUnMount: PropTypes.bool,
  nextBtnText: PropTypes.string,
  backBtnText: PropTypes.string,
  enabledNextButton: PropTypes.bool,
  isPMABank: PropTypes.bool,
  isPMCUser: PropTypes.bool,
  isSelectedPMAProperties: PropTypes.bool,
  isNewThreeFilterTabs: PropTypes.bool,
  isSubLease: PropTypes.bool,
};

ADGMProperties.defaultProps = {
  hidePropertyToggle: false,
  title: '',
  showTitle: false,
  requiredFilters: [],
  selectable: false,
  multi: false,
  switchType: null,
  isNewThreeFilterTabs: false,
  propertyType: null,
  onNext: null,
  onBack: null,
  defaultFilters: {},
  defaultSelected: null,
  defaultSwitchType: 'lease',
  state: {},
  isPMA: false,
  isMultiLease: false,
  showSwitch: false,
  showMortgageProperties: false,
  onNextLoading: false,
  viewLeaseUnitsHandler: null,
  clearPropertiesOnUnMount: false,
  nextBtnText: '',
  backBtnText: '',
  enabledNextButton: false,
  isPMABank: false,
  isPMCUser: false,
  isSelectedPMAProperties: false,
  isSubLease: false,
};

export default withStyles(styles)(memo(ADGMProperties));
